import { Component, Input, OnInit } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import { ApiUrl } from 'src/app/services/apiUrl';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-api-key-manager',
  templateUrl: './api-key-manager.component.html',
  styleUrls: ['./api-key-manager.component.scss']
})
export class ApiKeyManagerComponent implements OnInit {
  @Input() storeId: string = '';
  
  apiKey: any = null;
  loading: boolean = false;
  error: string | null = null;
  showKey: boolean = false;
  role: string | null = localStorage.getItem('role');
  debugInfo: string | null = null;

  constructor(
    private service: ApiService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService
  ) { }

  ngOnInit(): void {
    if (this.storeId) {
      this.fetchApiKey();
    }
  }

  ngOnChanges(): void {
    if (this.storeId) {
      this.fetchApiKey();
    }
  }

  fetchApiKey(): void {
    this.loading = true;
    this.spinner.show();
    this.debugInfo = null;
    
    this.service.getRequest(`${ApiUrl.getStoreApiKey}${this.storeId}`).subscribe(
      (res: any) => {
        console.log('Raw API response:', res);
        console.log('Response type:', typeof res);
        console.log('Has response property:', res.hasOwnProperty('response'));
        
        // Collect debug info
        const debugObj: any = {
          response_type: typeof res,
          has_response_prop: res.hasOwnProperty('response'),
          raw_response: JSON.stringify(res, null, 2).substring(0, 500) // Limit length for display
        };
        
        // Handle response if it's a string (might be JSON)
        let processedRes = res;
        if (typeof res === 'string') {
          try {
            processedRes = JSON.parse(res);
            console.log('Parsed string response:', processedRes);
            debugObj.parsed_string = JSON.stringify(processedRes, null, 2).substring(0, 500);
          } catch (e: any) {
            console.error('Failed to parse response string:', e);
            debugObj.parse_error = e.toString();
          }
        }
        
        // Check if response is nested
        const responseData = processedRes.response || processedRes;
        console.log('Processed response data:', responseData);
        debugObj.processed_data = JSON.stringify(responseData, null, 2).substring(0, 500);
        
        if (responseData.success) {
          console.log('API key data:', responseData.data);
          this.apiKey = responseData.data;
          this.error = null;
        } else {
          console.log('API key request unsuccessful:', responseData);
          this.apiKey = null;
          this.error = 'Failed to load API key';
          // Set debug info for display
          this.debugInfo = JSON.stringify(debugObj, null, 2);
        }
        this.loading = false;
        this.spinner.hide();
      },
      (err: any) => {
        console.error('API key request error:', err);
        this.apiKey = null;
        this.error = 'Failed to load API key';
        this.debugInfo = JSON.stringify({
          error_type: typeof err,
          error_obj: JSON.stringify(err, null, 2).substring(0, 500)
        }, null, 2);
        this.loading = false;
        this.spinner.hide();
      }
    );
  }

  generateApiKey(): void {
    if (!confirm('Are you sure? This will invalidate any existing API key for this store.')) {
      return;
    }
    
    this.loading = true;
    this.spinner.show();
    
    const data = { storeId: this.storeId };
    
    this.service.postRequest(ApiUrl.generateApiKey, data).subscribe(
      (res: any) => {
        // Check if response is nested
        const responseData = res.response || res;
        
        if (responseData.success) {
          this.apiKey = responseData.data;
          this.error = null;
          this.showKey = true;
          this.toastr.success('API key generated successfully');
        } else {
          this.error = responseData.message || 'Failed to generate API key';
          this.toastr.error(this.error || 'Failed to generate API key');
        }
        this.loading = false;
        this.spinner.hide();
      },
      (err: any) => {
        this.error = err.error?.message || 'Failed to generate API key';
        this.toastr.error(this.error || 'Failed to generate API key');
        this.loading = false;
        this.spinner.hide();
      }
    );
  }

  deactivateApiKey(): void {
    if (!confirm('Are you sure you want to deactivate this API key?')) {
      return;
    }
    
    this.loading = true;
    this.spinner.show();
    
    this.service.putRequest(`${ApiUrl.deactivateApiKey}${this.storeId}/deactivate`, {}).subscribe(
      (res: any) => {
        // Check if response is nested
        const responseData = res.response || res;
        
        if (responseData.success) {
          this.toastr.success('API key deactivated successfully');
          this.fetchApiKey(); // Refresh the key data
        } else {
          this.error = responseData.message || 'Failed to deactivate API key';
          this.toastr.error(this.error || 'Failed to deactivate API key');
        }
        this.loading = false;
        this.spinner.hide();
      },
      (err: any) => {
        this.error = err.error?.message || 'Failed to deactivate API key';
        this.toastr.error(this.error || 'Failed to deactivate API key');
        this.loading = false;
        this.spinner.hide();
      }
    );
  }

  copyToClipboard(): void {
    if (this.apiKey && this.apiKey.apiKey) {
      const el = document.createElement('textarea');
      el.value = this.apiKey.apiKey;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      this.toastr.success('API key copied to clipboard');
    }
  }

  toggleShowKey(): void {
    this.showKey = !this.showKey;
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  }
} 